import React from "react";
import { Seo } from "./Seo/Seo.tsx";
import { FOOTER_GROUPS, routesInGroup, findBySlug, localeOf } from "./Seo/routeSeo.ts";
import "./SiteFooter.css";
import { useTranslation } from "./i18n/useTranslation.ts";

/**
 * The all-pages index for one locale — every generator, chart, deck and quiz
 * written in that locale, grouped. `/all-worksheets` is the English one;
 * ADR 0051 §8 gives Spanish and French their own at their own slugs, each
 * rendered through this same template by `slug`.
 *
 * The hub of that locale's link graph since ADR 0027: the footer on every
 * page in a locale links its own index instead of listing every generator, so
 * this page carries the exhaustive list for that locale and every page in it
 * stays two clicks from every other.
 *
 * It is also the page a visitor wants when they do not know what the site has,
 * which is why it is a real page rather than a crawl artefact.
 *
 * scripts/prerender.js renders the same list into the static HTML - a crawler
 * must see it without running the bundle, which is the whole point.
 */
export function AllWorksheets({ slug }: { slug: string }) {
    const entry = findBySlug(slug);
    const t = useTranslation();

    if (!entry) {
        return null;
    }
    const locale = localeOf(entry);

    return (
        <>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                {entry.intro?.map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                ))}
            </div>

            <nav className="allWorksheets" aria-label={t('nav.allWorksheetsAria')}>
                {FOOTER_GROUPS.map((group) => {
                    const routes = routesInGroup(group.key, locale);
                    if (routes.length === 0) {
                        return null;
                    }
                    return (
                        <section className="allWorksheetsGroup" key={group.key}>
                            <h2>{group.heading}</h2>
                            <ul>
                                {routes.map((route) => (
                                    <li key={route.slug}>
                                        <a href={route.slug}>{route.h1}</a>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    );
                })}
            </nav>
        </>
    );
}

export default AllWorksheets;
